import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { Form, InputGroup } from 'react-bootstrap'
import { sha512 } from 'js-sha512'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faShuffle, faT } from '@fortawesome/free-solid-svg-icons'
import '../../styles/Input/InputText.css'

// eslint-disable-next-line
{/*
<InputText
    type={'text | name | email | phone | password'}
    theme={'light | dark'}
    ref={}
    icon={<FontAwesomeIcon icon={faT} />}
    text={} setText={}
    textError={} setTextError={}
    nullable={false}
    placeholder={""}
    maxLength={}
    passwordButtonVisible={false}
    passwordButtonGenerate={false}
    onPressEnter={() => }
/>
--inputTextLight: #ffffff;
--inputTextLightText: #49574b;
--inputTextDark: #eef5ef;
--inputTextDarkText: #49574b;
--inputTextSuccess: #435e48;
--inputTextError: #BB4040;
*/}

const InputText = forwardRef((props, ref) => {

    useImperativeHandle(ref, () => ({
        inputTextRef: inputTextRef,
        textValidation: (text) => textValidation(text),
        nameValidation: (name) => nameValidation(name),
        emailValidationOnBlur: (email) => emailValidationOnBlur(email),
        phoneValidation: (phone) => phoneValidation(phone),
        passwordValidation: (password) => passwordValidation(password),
    }))

    const inputTextRef = useRef(null)
    const [passwordVisible, setPasswordVisible] = useState(false)

    function onChange(text) {

        if (props.type === 'text') {
            textValidation(text)
        } else if (props.type === 'name') {
            nameFormat(text)
            nameValidation(text)
        } else if (props.type === 'email') {
            emailFormat(text)
            emailValidation(text)
        } else if (props.type === 'phone') {
            phoneValidation(text)
        } else if (props.type === 'password') {
            passwordValidation(text)
        }

    }

    function onBlur(text) {

        if (props.type === 'email') {
            emailValidationOnBlur(text)
        } else if (props.type === 'phone' && !props.textError) {
            phoneFormat(text)
        }

    }

    function textValidation(text) {

        if (props.nullable && text.length === 0) {
            props.setTextError(null)
            return true
        }
        if (text) {
            props.setTextError(false)
            return true
        } else {
            props.setTextError(true)
            return false
        }

    }

    function nameFormat(name) {

        if (name) {
            var nameFormat = name.toLowerCase().split(' ').map((item) => item.charAt(0).toUpperCase() + item.substring(1)).join(' ')
            nameFormat = nameFormat.split('-').map((item) => item.charAt(0).toUpperCase() + item.substring(1)).join('-')
            nameFormat = nameFormat.split('\'').map((item) => item.charAt(0).toUpperCase() + item.substring(1)).join('\'')
            props.setText(nameFormat)
        }

    }

    function nameValidation(name) {

        const nameRegex = /^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,.'-]+$/u
        if (props.nullable && name.length === 0) {
            props.setTextError(null)
            return true
        }
        if (name && nameRegex.test(name)) {
            props.setTextError(false)
            return true
        } else {
            props.setTextError(true)
            return false
        }

    }

    function emailFormat(email) {

        if (email) {
            props.setText(email.toLowerCase().trim())
        }

    }

    function emailValidationOnBlur(email) {

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (props.nullable && email.length === 0) {
            props.setTextError(null)
            return true
        }
        if (email && emailRegex.test(email)) {
            props.setTextError(false)
            return true
        } else {
            props.setTextError(true)
            return false
        }

    }

    function emailValidation(email) {

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (props.nullable && email.length === 0) {
            props.setTextError(null)
            return true
        }
        if (email && emailRegex.test(email)) {
            props.setTextError(false)
            return true
        }

    }

    function phoneValidation(phone) {

        const phoneRegex = /^[0-9 ]+$/
        if (props.nullable && phone.length === 0) {
            props.setTextError(null)
            return true
        }
        if (phone && (phone.length === 10 || phone.length === 14) && phoneRegex.test(phone)) {
            props.setTextError(false)
            return true
        } else {
            props.setTextError(true)
            return false
        }

    }

    function phoneFormat(phone) {

        if (phone.length === 10) {
            props.setText(phone[0] + phone[1] + " " + phone[2] + phone[3] + " " + phone[4] + phone[5] + " " + phone[6] + phone[7] + " " + phone[8] + phone[9])
        }

    }

    function getRandomPassword() {

        const date = new Date().toString()
        const passwordGenerate = sha512(date).slice(26, 46)
        props.setText(passwordGenerate)
        props.setTextError(false)

    }

    function passwordValidation(password) {

        if (password) {
            props.setTextError(false)
            return true
        } else {
            props.setTextError(true)
            return false
        }

    }

    return (

        <InputGroup>
            <InputGroup.Text className={['inputTextIcon', props.theme === 'light' ? 'inputTextIconLight' : 'inputTextIconDark', props.textError === false ? 'inputTextIconSuccess' : props.textError ? 'inputTextIconError' : null].join(" ")}>
                {props.icon ? props.icon : <FontAwesomeIcon icon={faT} />}
            </InputGroup.Text>
            <Form.Control
                ref={inputTextRef}
                value={props.text}
                type={props.type === 'email' ? 'email' : props.type === 'phone' ? 'tel' : (props.type === 'password' && !passwordVisible) ? 'password' : 'text'}
                placeholder={props.placeholder}
                maxLength={props.maxLength}
                className={['inputText', props.theme === 'light' ? 'inputTextLight' : 'inputTextDark', props.textError === false ? 'inputTextSuccess' : props.textError ? 'inputTextError' : null].join(" ")}
                onChange={(e) => { props.setText(e.target.value); onChange(e.target.value) }}
                onBlur={(e) => onBlur(e.target.value)}
                onKeyPress={(event) => props.onPressEnter && event.key === "Enter" ? props.onPressEnter() : null}
            />
            {props.type === 'password' && props.passwordButtonVisible ?
                <>
                    <InputGroup.Text
                        className={['inputTextVisible', props.textError === null ? 'inputTextVisible' : props.textError === false ? 'inputTextVisibleSuccess' : 'inputTextVisibleError', !props.passwordButtonGenerate ? 'inputTextVisibleLast' : null].join(" ")}
                        onClick={() => setPasswordVisible(!passwordVisible)}>
                        <FontAwesomeIcon icon={faEye} />
                    </InputGroup.Text>
                </>
                : null
            }
            {props.type === 'password' && props.passwordButtonGenerate ?
                <>
                    <InputGroup.Text className={['inputTextGenerate', props.textError === null ? 'inputTextGenerate' : props.textError === false ? 'inputTextGenerateSuccess' : 'inputTextGenerateError'].join(" ")} onClick={() => getRandomPassword()}>
                        <FontAwesomeIcon icon={faShuffle} />
                    </InputGroup.Text>
                </>
                : null
            }
        </InputGroup>

    )

})

export default InputText
